import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { ContractList } from "@cx/contracts";
import { CONTRACT_STATUSES, formatBRL, formatCompetence } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { CONTRACT_STATUS_LABEL, CONTRACT_STATUS_TONE, PERIODICITY_LABEL } from "./labels";

/** Serviços › Contratos (RF-SER-01): contratos por número, cliente e situação. */
export function ContractsListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const term = q.trim();
  const canCreate = can(perms, "contracts", "create");
  const list = useInfiniteQuery({
    queryKey: ["contracts", companyId, "list", term, status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ContractList>(
        `/contracts?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(status ? { status } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const create = () => navigate({ to: "/servicos/contratos/novo" });

  return (
    <>
      <ContentHeader
        title="Contratos"
        breadcrumb={["Serviços", "Contratos"]}
        actions={
          <>
            {can(perms, "contracts", "read") && (
              <Link to="/servicos/cobrancas" className="btn btn-outline-secondary">
                Cobranças
              </Link>
            )}
            {canCreate && (
              <button type="button" className="btn btn-primary" onClick={create}>
                Incluir contrato
              </button>
            )}
          </>
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-5">
            <label className="visually-hidden" htmlFor="ct-q">
              Pesquisar contrato
            </label>
            <input
              id="ct-q"
              type="search"
              className="form-control"
              placeholder="Número, cliente ou descrição"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="visually-hidden" htmlFor="ct-status">
              Situação
            </label>
            <select
              id="ct-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Todas as situações</option>
              {CONTRACT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {CONTRACT_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum contrato"
            description="Cadastre contratos de serviço para gerar as cobranças do mês (conta a receber, Pix e NFS-e)."
            actions={canCreate ? [{ label: "Incluir contrato", onClick: create }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Contratos">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Cliente</th>
                    <th scope="col">Descrição</th>
                    <th scope="col">Periodicidade</th>
                    <th scope="col">Vencimento</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col">Última cobrança</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to="/servicos/contratos/$id" params={{ id: r.id }}>
                          {r.number}
                        </Link>
                      </td>
                      <td>
                        {r.contact.name}
                        {r.billTo && <div className="small text-muted">Cobrar de {r.billTo.name}</div>}
                      </td>
                      <td>{r.description ?? "—"}</td>
                      <td>{PERIODICITY_LABEL[r.periodicity]}</td>
                      <td>Dia {r.dueDay}</td>
                      <td className="text-end">{formatBRL(r.amountCents)}</td>
                      <td>{r.lastCompetence ? formatCompetence(r.lastCompetence) : "—"}</td>
                      <td>
                        <StatusBadge tone={CONTRACT_STATUS_TONE[r.status]}>
                          {CONTRACT_STATUS_LABEL[r.status]}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage && (
              <div className="p-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
